import React from "react";
import { useAuth } from "./useAuth";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import CyCraftLogo from "../../assets/CyCraftLogo.svg";
import loginBackground from "../../assets/xecguard-login-bg-v2.jpg";
import { isGatewayIdp, isGoogle } from "./idp";
import { googleAuthConfig } from "./googleConfig";

const Page = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${loginBackground});
  background-size: cover;
  background-position: center;
`;

const LogoLink = styled(RouterLink)`
  position: absolute;
  top: 20px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  z-index: 1;
`;

const LogoImg = styled.img`
  height: 32px;
`;

const Card = styled.div`
  position: absolute;
  top: 50%;
  left: 235px;
  transform: translateY(-50%);
  width: 375px;
  padding: 36px;
  background: rgba(73, 67, 88, 0.5);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 36px;
  backdrop-filter: blur(4px);
`;

const Title = styled.h2`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 42px;
  line-height: 1.25;
  color: var(--x-gray-200, #f6f6f7);
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.4;
  color: var(--x-gray-400, #cfd0d3);
  margin: 0;

  &:lang(ja) {
    font-size: 17px;
  }
`;

const SignInButton = styled.button`
  width: 100%;
  padding: 9px 16px;
  background: var(--x-red, #ff595a);
  border: 2px solid var(--x-red, #ff595a);
  border-radius: 4px;
  color: var(--x-gray-200, #f6f6f7);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  text-align: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const LoginPage = (): JSX.Element => {
  const auth = useAuth();
  const { t } = useTranslation("common");

  // Entra: force the Microsoft account picker so users with multiple MS accounts
  // can choose, instead of being silently SSO'd into whichever account is active.
  // Cognito has no equivalent, so sign in unchanged.
  const handleSignIn = (): void => {
    // `prompt=select_account` forces the account picker instead of silently
    // reusing whichever session is active — the same reason Entra needs it.
    // Google additionally takes `hd`, which filters the picker to the Workspace
    // domain. It is a UX hint only; the security boundary is the backend's `hd`
    // CLAIM assertion (see googleConfig).
    const extraQueryParams: Record<string, string> = { prompt: "select_account" };
    if (isGoogle && googleAuthConfig.extraQueryParams?.hd) {
      extraQueryParams.hd = googleAuthConfig.extraQueryParams.hd;
    }
    auth.signinRedirect(isGatewayIdp ? { extraQueryParams } : undefined);
  };

  // Leave /login once a session exists.
  //
  // This is not redundant with Callback.tsx — it is the case Callback.tsx
  // cannot reach. Cognito and Entra sign in by NAVIGATING: the browser leaves
  // for the IdP and comes back to /callback, and that component is what sends
  // an authenticated user on to /dashboard. So /login never needed a rule for
  // "already signed in"; on those IdPs you are simply never on it.
  //
  // GIS does not navigate at all. The credential arrives through a JavaScript
  // callback on THIS page, `adopt()` sets state, and isAuthenticated flips
  // while the browser is still sitting on /login. /callback is never visited,
  // so nothing moves.
  //
  // The failure is quiet and misleading: OrgProvider wraps the router, so its
  // GET /me and GET /licenses fire on isAuthenticated regardless of route. The
  // server log fills with 200s while the screen stays on the login card —
  // which reads as a backend problem and is not one. Observed on dev1,
  // 2026-08-08.
  //
  // Checked before isLoading so a session restored from sessionStorage on
  // reload does not flash the login background on its way out.
  if (auth.isAuthenticated) {
    // Same destination as Callback.tsx. /dashboard is wrapped in
    // ProtectedRoute, which owns the org gate (pending / needs-org /
    // forbidden), so this hands off rather than duplicating that decision.
    return <Navigate to="/dashboard" replace />;
  }

  if (auth.isLoading) {
    return (
      <Page>
        <Background />
      </Page>
    );
  }

  return (
    <Page>
      <Background />
      <LogoLink to="/login">
        <LogoImg src={CyCraftLogo} alt={t("auth.logoAlt")} />
      </LogoLink>
      <Card>
        <TextGroup>
          <Title>
            {t("auth.welcome")}
            <br />
            {t("auth.product")}
          </Title>
          <Subtitle>{t("auth.tagline")}</Subtitle>
        </TextGroup>
        <ButtonGroup>
          {/* One button for every IdP again.

              This used to branch: Google got a <div> that GIS painted its own
              button into, because GIS would only fire from a control it
              rendered. That constraint came with GIS, and GIS is gone — the
              flow is now a plain authorize redirect the app triggers itself,
              so Google needs no more special-casing here than Entra does. */}
          <SignInButton onClick={handleSignIn}>
            {t("auth.signIn")}
          </SignInButton>
        </ButtonGroup>
      </Card>
    </Page>
  );
};

export default LoginPage;
